import { CurrencyPipe } from '@angular/common';
import * as moment from 'moment';

import { isDefAndNotNull } from '@app/utils/def';
import { AreaPageElementQuestionModel } from '@main-application/shared/template-editor-dialog/models/template-editor-model';
import { RestTicketModel } from '@shared/interfaces/turnover.interface';

import { InspectionPage } from './inspection-area.model';
import { PropertyState } from './inspection.model';

export class InspectionStepModel {
  readonly propertyState: PropertyState;
  readonly propertyStateCount: number;
  readonly fileUploadCount: number;
  readonly notesCount: number;
  readonly imagesCount: number;
  note: string;
  readonly files: { fileUploadId: number }[];
  readonly questions: [string, boolean, string, string, { label: string; group?: string; color?: string }][];
  readonly emoji: string;
  readonly emojiName: string;
  readonly emojiCode: string;
  readonly stepName: string;
  readonly quantity: string;
  readonly hours: string;
  readonly tickets: RestTicketModel[];

  constructor(
    public readonly page: InspectionPage,
    tickets: RestTicketModel[],
    public readonly areaName = '',
    public readonly areaId: number,
    public readonly index: number
  ) {
    this.tickets = tickets?.filter(t => t.surveyPageId === index || t.surveyPageId === undefined) || [];
    const noteElms = page.elements?.filter(elm => elm.type?.toLowerCase() === 'comment');
    const fileElms = page.elements?.filter(elm => elm.type?.toLowerCase() === 'file');
    const stateElms = page.elements?.filter(elm => elm.type?.toLowerCase() === 'propertystate');
    const quantityElms = page.elements?.filter(elm => elm.type?.toLowerCase() === 'quantity');
    const hoursElms = page.elements?.filter(elm => elm.type?.toLowerCase() === 'hours');

    let questionsElms = [];
    const panel = page.elements?.find(elm => elm.type === 'panel');
    if (panel) {
      questionsElms = page.elements?.find(elm => elm.type === 'panel' && elm.elements?.length)?.elements;
    } else {
      questionsElms = page.elements?.filter(elm => ![...noteElms, ...fileElms, ...stateElms].includes(elm));
    }

    this.stepName = page.title || '';
    this.questions =
      questionsElms
        ?.filter(qElm => qElm.type?.toLowerCase() !== 'quantity' && qElm.type?.toLowerCase() !== 'hours')
        ?.filter(
          qElm => isDefAndNotNull(qElm.value) || isDefAndNotNull(qElm.title) || isDefAndNotNull(qElm.defaultValue)
        )
        ?.map(qElm => {
          let boolValue: boolean | undefined = undefined;

          if (qElm.type?.toLowerCase() === 'boolean' && (qElm.value === 'true' || qElm.value === 'false')) {
            if (isDefAndNotNull(qElm.value)) {
              boolValue = qElm.value === 'true';
            } else if (isDefAndNotNull(qElm.defaultValue)) {
              boolValue = qElm.defaultValue === 'true';
            }
          }

          return [qElm.title, boolValue, qElm.type, qElm.customFieldBinding, setDefaultValue(qElm)];
        }) || [];

    this.notesCount = noteElms?.length ?? 0;
    this.fileUploadCount = fileElms?.length ?? 0;
    this.propertyStateCount = stateElms?.length ?? 0;
    this.imagesCount = page.mainImage?.length ?? 0;

    this.note = noteElms?.[0]?.value ?? '';
    this.files = page.mainImage;
    if (this.propertyStateCount > 0) {
      this.propertyState = stateElms[0].value as PropertyState;
      if (stateElms[0].items?.length) {
        const elementItem = stateElms[0].items.find(x => x.item.id === this.propertyState);
        if (elementItem) {
          const emojiCode = Number('0x' + elementItem.item.emoji);
          if (!isNaN(emojiCode)) {
            this.emoji = String.fromCodePoint(emojiCode);
            this.emojiName = elementItem.item.text;
            this.emojiCode = elementItem.item.emoji;
          }

          if (quantityElms?.length && elementItem.item.showQtyWidget) {
            this.quantity = quantityElms[0].value;
          }

          if (hoursElms?.length && elementItem.item.onSelect?.includes('showHoursWidget')) {
            if (hoursElms[0].value) {
              //TODO: convert to pipe? https://stackoverflow.com/a/50558928 but we need to save it as 00:00 to auto pass it to ticket creation
              const value = parseInt(hoursElms[0].value);
              const minutes = Math.floor(value / 60);

              this.hours =
                minutes.toString().padStart(2, '0') + ':' + (value - minutes * 60).toString().padStart(2, '0');
            } else {
              this.hours = '0';
            }
          }
        }
      } else {
        const { emoji, emojiCode, emojiName } = this.getDefaultEmojiCode(this.propertyState);
        this.emoji = emoji;
        this.emojiCode = emojiCode;
        this.emojiName = emojiName;
      }
    }
  }

  get isCustomStep() {
    return this.notesCount === 1 && this.fileUploadCount === 1 && this.propertyStateCount === 0;
  }

  getDefaultEmojiCode(state: PropertyState) {
    switch (state) {
      case 'good':
        return { emoji: String.fromCodePoint(Number('0x1f60d')), emojiCode: '1f60d', emojiName: 'Good' };
      case 'fair':
        return { emoji: String.fromCodePoint(Number('0x1f610')), emojiCode: '1f610', emojiName: 'Fair' };
      case 'poor':
        return { emoji: String.fromCodePoint(Number('0x1f4a9')), emojiCode: '1f4a9', emojiName: 'Poor' };
    }
    return {};
  }

  get flagsCount() {
    return 0; // TODO
  }
}

export function setDefaultValue(
  item: AreaPageElementQuestionModel
): { label: string; group?: string; color?: string } | undefined {
  const resolvedValue = item.value ?? item.defaultValue;
  const currencyPipe = new CurrencyPipe('en-US');

  if (!item.hasOwnProperty('defaultValue') && !item.hasOwnProperty('value')) return undefined;
  if (resolvedValue === 'null') return undefined;

  let color: string | undefined;
  let group: string | undefined;
  let label: string | undefined;

  if (item.type === 'dropdown' && resolvedValue && item.options?.length > 0) {
    const option = item.options.find(el => el.value === resolvedValue);
    color = option?.color ? option.color : undefined;
    group = option?.group ? option.group : undefined;
    label = option?.label ? option.label : undefined;
  } else if (item.type?.toLowerCase() === 'checklist' && resolvedValue && item.options?.length > 0) {
    const values = (resolvedValue as string).split(',').map(v => v.trim());
    const matchedOptions = item.options.filter(opt => values.includes(opt.value));
    label = matchedOptions.map(opt => opt.label).join(', ');
  } else if (item.type === 'money' && resolvedValue) {
    label = currencyPipe.transform(resolvedValue as string, 'USD', 'symbol', '1.0-0');
  } else if (
    item.type === 'boolean' &&
    (typeof resolvedValue === 'boolean' || resolvedValue === 'true' || resolvedValue === 'false')
  ) {
    label = undefined;
  } else if (item.type === 'date' && resolvedValue) {
    label = normalizeDate(resolvedValue as string);
  } else if (item.type?.toLowerCase() === 'datetime' && resolvedValue) {
    label = normalizeDate(resolvedValue as string, true);
  } else {
    label = resolvedValue ? `${resolvedValue}` : undefined;
  }

  return label ? { label, group, color } : undefined;
}

function normalizeDate(value: string, useTime = false): string | undefined {
  if (!value) return undefined;

  if (moment(value, 'YYYY-MM-DD', true).isValid()) {
    return useTime
      ? moment(value, 'YYYY-MM-DD').format('MM/DD/YY h:mm A')
      : moment(value, 'YYYY-MM-DD').format('MM/DD/YY');
  }

  const m = moment(value);
  if (m.isValid()) {
    return useTime ? m.format('MM/DD/YY h:mm A') : m.format('MM/DD/YY');
  }

  return undefined;
}
